import React, { useState } from 'react';
import { 
  Plus, 
  Activity, 
  Heart, 
  Thermometer, 
  Scale, 
  TrendingUp,
  TrendingDown,
  Calendar,
  BarChart3
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import './HealthTracker.css';

const HealthTracker = () => {
  const [healthData, setHealthData] = useState([
    {
      id: 1,
      date: '2024-01-15',
      bloodPressure: { systolic: 120, diastolic: 80 },
      heartRate: 72,
      temperature: 98.6,
      weight: 70.5,
      notes: 'Feeling good today'
    },
    {
      id: 2,
      date: '2024-01-14',
      bloodPressure: { systolic: 118, diastolic: 78 },
      heartRate: 70,
      temperature: 98.4,
      weight: 70.2,
      notes: 'Regular check'
    },
    {
      id: 3,
      date: '2024-01-13',
      bloodPressure: { systolic: 125, diastolic: 82 },
      heartRate: 75,
      temperature: 98.8,
      weight: 70.8,
      notes: 'Slightly elevated BP'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    systolic: '',
    diastolic: '',
    heartRate: '',
    temperature: '',
    weight: '',
    notes: ''
  });

  const handleAddHealthData = () => {
    if (!formData.systolic || !formData.diastolic || !formData.heartRate) {
      toast.error('Please fill in all required fields');
      return;
    }

    const newHealthData = {
      id: Date.now(),
      date: formData.date,
      bloodPressure: {
        systolic: parseInt(formData.systolic),
        diastolic: parseInt(formData.diastolic)
      },
      heartRate: parseInt(formData.heartRate),
      temperature: formData.temperature ? parseFloat(formData.temperature) : null,
      weight: formData.weight ? parseFloat(formData.weight) : null,
      notes: formData.notes
    };

    setHealthData([newHealthData, ...healthData]);
    setFormData({
      date: format(new Date(), 'yyyy-MM-dd'),
      systolic: '',
      diastolic: '',
      heartRate: '',
      temperature: '',
      weight: '',
      notes: ''
    });
    setShowAddForm(false);
    toast.success('Health data added successfully!');
  };

  const getLatestData = () => {
    if (healthData.length === 0) return null;
    return healthData[0];
  };

  const getTrend = (current, previous) => {
    if (!previous) return 'stable';
    if (current > previous) return 'up';
    if (current < previous) return 'down';
    return 'stable';
  };

  const getBPStatus = (systolic, diastolic) => {
    if (systolic < 120 && diastolic < 80) return 'normal';
    if (systolic >= 120 && systolic < 130 && diastolic < 80) return 'elevated';
    if (systolic >= 130 || diastolic >= 80) return 'high';
    return 'normal';
  };

  const getHeartRateStatus = (rate) => {
    if (rate < 60) return 'low';
    if (rate >= 60 && rate <= 100) return 'normal';
    return 'high';
  };

  const latestData = getLatestData();

  return (
    <div className="container">
      <div className="page-header">
        <h1 className="page-title">Health Tracker</h1>
        <p className="page-subtitle">Monitor your vital signs and health metrics</p>
      </div>

      <div className="health-tracker-container">
        {latestData && (
          <div className="current-stats">
            <h2 className="section-title">Today's Overview</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <Heart size={32} color="#ff4757" />
                </div>
                <div className="stat-value">{latestData.bloodPressure.systolic}/{latestData.bloodPressure.diastolic}</div>
                <div className="stat-label">Blood Pressure (mmHg)</div>
                <div className={`stat-status ${getBPStatus(latestData.bloodPressure.systolic, latestData.bloodPressure.diastolic)}`}>
                  {getBPStatus(latestData.bloodPressure.systolic, latestData.bloodPressure.diastolic)}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <Activity size={32} color="#2ed573" />
                </div>
                <div className="stat-value">{latestData.heartRate}</div>
                <div className="stat-label">Heart Rate (bpm)</div>
                <div className={`stat-status ${getHeartRateStatus(latestData.heartRate)}`}>
                  {getHeartRateStatus(latestData.heartRate)}
                </div>
              </div>

              {latestData.temperature && (
                <div className="stat-card">
                  <div className="stat-icon">
                    <Thermometer size={32} color="#ffa502" />
                  </div>
                  <div className="stat-value">{latestData.temperature}°F</div>
                  <div className="stat-label">Temperature</div>
                </div>
              )}

              {latestData.weight && (
                <div className="stat-card">
                  <div className="stat-icon">
                    <Scale size={32} color="#3742fa" />
                  </div>
                  <div className="stat-value">{latestData.weight} kg</div>
                  <div className="stat-label">Weight</div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="health-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            <Plus size={16} />
            Log Health Data
          </button>
        </div>

        {showAddForm && (
          <div className="add-health-form">
            <h3>Log New Health Data</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Systolic BP (mmHg) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.systolic}
                  onChange={(e) => setFormData({...formData, systolic: e.target.value})}
                  placeholder="e.g., 120"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Diastolic BP (mmHg) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.diastolic}
                  onChange={(e) => setFormData({...formData, diastolic: e.target.value})}
                  placeholder="e.g., 80"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Heart Rate (bpm) *</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.heartRate}
                  onChange={(e) => setFormData({...formData, heartRate: e.target.value})}
                  placeholder="e.g., 72"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Temperature (°F)</label>
                <input
                  type="number"
                  step="0.1"
                  className="form-input"
                  value={formData.temperature}
                  onChange={(e) => setFormData({...formData, temperature: e.target.value})}
                  placeholder="e.g., 98.6"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Weight (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  className="form-input"
                  value={formData.weight}
                  onChange={(e) => setFormData({...formData, weight: e.target.value})}
                  placeholder="e.g., 70.5"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="How are you feeling today?"
                rows="3"
              />
            </div>

            <div className="form-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setFormData({
                    date: format(new Date(), 'yyyy-MM-dd'),
                    systolic: '',
                    diastolic: '',
                    heartRate: '',
                    temperature: '',
                    weight: '',
                    notes: ''
                  });
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleAddHealthData}
              >
                Log Data
              </button>
            </div>
          </div>
        )}

        <div className="health-history">
          <h2 className="section-title">Health History</h2>
          
          <div className="health-data-list">
            {healthData.map((data, index) => {
              const previousData = healthData[index + 1];
              const bpTrend = getTrend(data.bloodPressure.systolic, previousData?.bloodPressure.systolic);
              const hrTrend = getTrend(data.heartRate, previousData?.heartRate);
              
              return (
                <div key={data.id} className="health-data-card">
                  <div className="data-header">
                    <div className="data-date">
                      <Calendar size={16} />
                      {format(new Date(data.date), 'MMM dd, yyyy')}
                    </div>
                  </div>

                  <div className="data-metrics">
                    <div className="metric-item">
                      <div className="metric-label">Blood Pressure</div>
                      <div className="metric-value">
                        {data.bloodPressure.systolic}/{data.bloodPressure.diastolic} mmHg
                        {bpTrend !== 'stable' && (
                          <span className={`trend ${bpTrend}`}>
                            {bpTrend === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                          </span>
                        )}
                      </div>
                      <div className={`metric-status ${getBPStatus(data.bloodPressure.systolic, data.bloodPressure.diastolic)}`}>
                        {getBPStatus(data.bloodPressure.systolic, data.bloodPressure.diastolic)}
                      </div>
                    </div>

                    <div className="metric-item">
                      <div className="metric-label">Heart Rate</div>
                      <div className="metric-value">
                        {data.heartRate} bpm
                        {hrTrend !== 'stable' && (
                          <span className={`trend ${hrTrend}`}>
                            {hrTrend === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                          </span>
                        )}
                      </div>
                      <div className={`metric-status ${getHeartRateStatus(data.heartRate)}`}>
                        {getHeartRateStatus(data.heartRate)}
                      </div>
                    </div>

                    {data.temperature && (
                      <div className="metric-item">
                        <div className="metric-label">Temperature</div>
                        <div className="metric-value">{data.temperature}°F</div>
                      </div>
                    )}

                    {data.weight && (
                      <div className="metric-item">
                        <div className="metric-label">Weight</div>
                        <div className="metric-value">{data.weight} kg</div>
                      </div>
                    )}
                  </div>

                  {data.notes && (
                    <div className="data-notes">
                      <strong>Notes:</strong> {data.notes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {healthData.length === 0 && (
            <div className="empty-state">
              <BarChart3 size={64} className="empty-state-icon" />
              <h3>No health data logged yet</h3>
              <p>Start tracking your vital signs to monitor your health</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddForm(true)}
              >
                <Plus size={16} />
                Log Your First Entry
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthTracker; 